import { Badge, Text } from '@chakra-ui/react'
import { DataColumn } from '@hb/react'
import {
  CollectionFilter,
  getDateString,
  LAB_RESULTS,
  LabResult,
  LabResultFlag,
  USERS,
  WithMetadata,
} from '@hb/shared'
import { DataListTab } from '../../../../types/data'
import { SortButton } from '../../../DataList/SortButton'

export type LabResultItem = WithMetadata<LabResult>
type LabResultColumn = DataColumn<LabResultItem>

const flagColorScheme: Record<LabResultFlag, string> = {
  normal: 'gray',
  high: 'red',
  low: 'orange',
  abnormal: 'purple',
  critical: 'red',
}

const flagLabel: Record<LabResultFlag, string> = {
  normal: 'Normal',
  high: 'HIGH',
  low: 'LOW',
  abnormal: 'ABN',
  critical: 'CRITICAL',
}

const HeaderText = ({ children }: { children: string }) => (
  <Text pl={2} fontWeight={600} fontSize="sm" color="gray.600">
    {children}
  </Text>
)

const labNameColumn: LabResultColumn = {
  title: 'Lab',
  width: 170,
  Header: () => <HeaderText>Lab</HeaderText>,
  Render: ({ data: { labName } }) => (
    <Text isTruncated fontWeight={600} fontSize="sm" px={2}>
      {labName || 'Unnamed lab'}
    </Text>
  ),
}

const resultColumn: LabResultColumn = {
  title: 'Result',
  Header: () => <HeaderText>Result</HeaderText>,
  Render: ({ data: { result, unit, referenceRange } }) => (
    <Text isTruncated fontSize="sm" px={2}>
      {result}
      {unit ? ` ${unit}` : ''}
      {referenceRange ? ` (ref: ${referenceRange})` : ''}
    </Text>
  ),
}

const flagColumn: LabResultColumn = {
  title: 'Flag',
  width: 100,
  Header: () => <HeaderText>Flag</HeaderText>,
  Render: ({ data: { flagStatus } }) => (
    <Badge mx={2} colorScheme={flagColorScheme[flagStatus] ?? 'gray'} fontSize="xs">
      {flagLabel[flagStatus] ?? '—'}
    </Badge>
  ),
}

const statusColumn: LabResultColumn = {
  title: 'Status',
  width: 130,
  Header: () => <HeaderText>Status</HeaderText>,
  Render: ({ data: { flagStatus, acknowledgedAt } }) => {
    if (acknowledgedAt)
      return (
        <Badge mx={2} colorScheme="green" fontSize="xs">
          Acknowledged
        </Badge>
      )
    if (flagStatus && flagStatus !== 'normal')
      return (
        <Badge mx={2} colorScheme="orange" fontSize="xs">
          Needs review
        </Badge>
      )
    return (
      <Text fontSize="xs" color="gray.400" px={2}>
        —
      </Text>
    )
  },
}

const dateColumn: LabResultColumn = {
  title: 'Date',
  width: 120,
  sortKey: 'createdOn',
  defaultSortDirection: 'desc',
  Header: () => (
    <SortButton defaultDir="desc" sortKey="createdOn">
      <HeaderText>Date</HeaderText>
    </SortButton>
  ),
  Render: ({ data: { resultDate, collectedDate, createdOn } }) => (
    <Text fontSize="sm" px={2}>
      {resultDate
        ? getDateString(resultDate, 'short')
        : collectedDate
          ? getDateString(collectedDate, 'short')
          : getDateString(createdOn, 'short', false)}
    </Text>
  ),
}

// assessmentId scopes to that pregnancy when set; null shows all of the patient's results.
// practiceId (providers-app) additionally scopes to the viewing practice.
export const getLabResultsTab = (
  patientId: string,
  assessmentId: string | null,
  practiceId: string | null,
): DataListTab<LabResultItem> => ({
  baseQuery: {
    collection: `${USERS}/${patientId}/${LAB_RESULTS}`,
    filters: [
      ...(assessmentId
        ? [['assessmentId', '==', assessmentId] as CollectionFilter<LabResultItem>]
        : []),
      ...(practiceId ? [['practiceId', '==', practiceId] as CollectionFilter<LabResultItem>] : []),
    ],
  },
  columns: {
    lab: labNameColumn,
    result: resultColumn,
    flag: flagColumn,
    status: statusColumn,
    date: dateColumn,
  },
  defaultSort: {
    field: 'createdOn',
    order: 'desc',
  },
  itemHeight: 40,
  itemName: 'lab result',
})
